import { ROUTES } from 'utils/constants';

/** JSON: string[] of canonical pathnames, newest first (no query strings). */
export const QUICK_LINKS_RECENT_PREFIX = 'quick_links_recent_v1';

const MAX_PATHS = 24;

/**
 * Every path we can record or show. Longer paths must be listed so prefix matching prefers longer paths first (e.g. nested routes before parents).
 */
export const QUICK_LINK_CANONICAL_PATHS: readonly string[] = [
  ROUTES.REPORTS.INVENTORY_REPORT,
  ROUTES.REPORTS.ROOT,
  ROUTES.PERMISSION.ROOT,
  ROUTES.ROLE.ROOT,
  ROUTES.KANBAN,
  ROUTES.USER.PROFILE,
  ROUTES.COMPANIES.ROOT,
];

const ALLOWED = new Set<string>(QUICK_LINK_CANONICAL_PATHS);

const SORTED_LONGEST_FIRST = [...QUICK_LINK_CANONICAL_PATHS].sort(
  (a, b) => b.length - a.length,
);

function storageKey(companyId: string): string {
  return `${QUICK_LINKS_RECENT_PREFIX}:${companyId}`;
}

/**
 * Maps a visited pathname to one canonical quick-link path, or null if not tracked.
 */
export function pathnameToCanonicalQuickPath(pathname: string): string | null {
  if (pathname === ROUTES.DASHBOARD) return null;

  for (const p of SORTED_LONGEST_FIRST) {
    if (pathname === p || pathname.startsWith(`${p}/`)) {
      return p;
    }
  }

  return null;
}

function parsePathList(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of parsed) {
      if (typeof item !== 'string') continue;
      const c = pathnameToCanonicalQuickPath(item) ?? item;
      if (!ALLOWED.has(c)) continue;
      if (seen.has(c)) continue;
      seen.add(c);
      out.push(c);
    }
    return out;
  } catch {
    return [];
  }
}

export function readQuickLinkRecentPaths(companyId: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    return parsePathList(localStorage.getItem(storageKey(companyId)));
  } catch {
    return [];
  }
}

const QUICK_LINKS_EVENT = 'quick-links-recent-updated';

export function subscribeQuickLinksRecent(listener: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }
  const onCustom = () => listener();
  const onStorage = (e: StorageEvent) => {
    if (e.key?.startsWith(QUICK_LINKS_RECENT_PREFIX)) listener();
  };
  window.addEventListener(QUICK_LINKS_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(QUICK_LINKS_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}

function notifyQuickLinksRecentUpdated(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(QUICK_LINKS_EVENT));
}

export function recordQuickLinkVisit(
  companyId: string,
  pathname: string,
): void {
  if (typeof window === 'undefined') return;
  const canonical = pathnameToCanonicalQuickPath(pathname);
  if (!canonical || !ALLOWED.has(canonical)) return;

  const key = storageKey(companyId);
  let list = parsePathList(localStorage.getItem(key));
  list = list.filter((p) => p !== canonical);
  list.unshift(canonical);
  list = list.slice(0, MAX_PATHS);
  try {
    localStorage.setItem(key, JSON.stringify(list));
    notifyQuickLinksRecentUpdated();
  } catch {
    // ignore
  }
}
